/**
 * @fileoverview SortingHat Component - Handles the magical house assignment process
 *
 * Features:
 * - Balanced algorithm: Maintains house populations within ±1 student
 * - Optional manual house join that still plays the sorting ceremony
 * - Prevents duplicate sorting for already-assigned students
 * - Animated sorting experience with hat imagery
 * - Automatic persistence to both localStorage and backend database
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser } from '../App.jsx';

const HOGWARTS_HOUSES = ['Gryffindor', 'Hufflepuff', 'Ravenclaw', 'Slytherin'];

const HOUSE_COLORS = {
  Gryffindor: '#740001',
  Hufflepuff: '#ECBA1A',
  Ravenclaw: '#2E5C8A',
  Slytherin: '#1A472A',
};

const HOUSE_TEXT = {
  Gryffindor: '#f8fafc',
  Hufflepuff: '#1c1917',
  Ravenclaw: '#f8fafc',
  Slytherin: '#f8fafc',
};

export default function SortingHat() {
  const navigate = useNavigate();
  const [assignedHouse, setAssignedHouse] = useState(null);
  const [isPerformingCeremony, setIsPerformingCeremony] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showManualJoin, setShowManualJoin] = useState(false);
  const [roster, setRoster] = useState([]);
  const [rosterError, setRosterError] = useState('');
  const [confirmHouse, setConfirmHouse] = useState(null);

  const performHouseAssignment = async (chosenHouse = null) => {
    const currentStudent = getCurrentUser();
    if (!currentStudent) return navigate('/');

    if (currentStudent.house) {
      alert('You have already been sorted into a house!');
      return navigate('/home');
    }

    setShowHelp(false);
    setShowManualJoin(false);
    setConfirmHouse(null);
    setIsPerformingCeremony(true);

    try {
      let selectedHouse = chosenHouse;

      if (!selectedHouse) {
        const studentsResponse = await fetch(`/api/students/${currentStudent.code}`);
        if (!studentsResponse.ok) throw new Error('Failed to fetch students');

        const studentRoster = await studentsResponse.json();
        if (!Array.isArray(studentRoster)) throw new Error('Invalid students data');

        const housePopulations = HOGWARTS_HOUSES.reduce((populationMap, houseName) => {
          populationMap[houseName] = studentRoster.filter(student => student.house === houseName).length;
          return populationMap;
        }, {});

        const balancedHouseCandidates = [];
        for (const houseName of HOGWARTS_HOUSES) {
          const simulatedPopulations = { ...housePopulations };
          simulatedPopulations[houseName] = (simulatedPopulations[houseName] || 0) + 1;
          const populationValues = Object.values(simulatedPopulations);
          const maxPopulation = Math.max(...populationValues);
          const minPopulation = Math.min(...populationValues);
          if (maxPopulation - minPopulation <= 1) balancedHouseCandidates.push(houseName);
        }

        if (balancedHouseCandidates.length > 0) {
          selectedHouse = balancedHouseCandidates[Math.floor(Math.random() * balancedHouseCandidates.length)];
        } else {
          const minimumPopulation = Math.min(...Object.values(housePopulations));
          const leastPopulatedHouses = HOGWARTS_HOUSES.filter(house => housePopulations[house] === minimumPopulation);
          selectedHouse = leastPopulatedHouses[Math.floor(Math.random() * leastPopulatedHouses.length)];
        }
      }

      const updatedStudent = { ...currentStudent, house: selectedHouse, schoolId: currentStudent.code };
      localStorage.setItem('hp_user', JSON.stringify(updatedStudent));

      const studentDataPayload = {
        id: updatedStudent.id,
        name: updatedStudent.name,
        house: updatedStudent.house,
        points: updatedStudent.points || 0,
        bonusPoints: updatedStudent.bonusPoints || 0,
        schoolId: updatedStudent.schoolId,
      };

      const updateResponse = await fetch(`/api/students/${updatedStudent.schoolId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ students: [studentDataPayload] }),
      });

      if (!updateResponse.ok) throw new Error('Failed to update student house');

      await new Promise(resolve => setTimeout(resolve, 5000));

      setIsPerformingCeremony(false);
      setAssignedHouse(selectedHouse);
    } catch (error) {
      console.error('House assignment error:', error);
      alert('Failed to assign house. Please try again.');
      setIsPerformingCeremony(false);
    }
  };

  const openManualJoin = async () => {
    const currentStudent = getCurrentUser();
    if (!currentStudent) return navigate('/');

    setShowHelp(false);
    setRosterError('');
    setShowManualJoin(true);

    try {
      const studentsResponse = await fetch(`/api/students/${currentStudent.code}`);
      if (!studentsResponse.ok) throw new Error('Failed to fetch students');
      const studentRoster = await studentsResponse.json();
      if (!Array.isArray(studentRoster)) throw new Error('Invalid students data');
      setRoster(studentRoster);
    } catch (error) {
      console.error('Failed to load houses:', error);
      setRosterError('Could not load the houses. Please try again.');
    }
  };

  const membersOf = (houseName) => roster
    .filter(student => (student.house || '').trim().toLowerCase() === houseName.toLowerCase())
    .map(student => ({ id: student.id, name: student.name || student.id }))
    .sort((a, b) => a.name.localeCompare(b.name));

  if (assignedHouse) {
    return (
      <div style={styles.resultsContainer}>
        <h2 style={styles.announcementTitle}>
          The Sorting Hat has decided!
        </h2>
        <div style={styles.houseRevealSection}>
          <img
            src={`/images/houses/${assignedHouse.toLowerCase()}.png`}
            alt={`${assignedHouse} house crest`}
            style={styles.houseCrestImage}
          />
          <h1 style={styles.houseNameTitle}>
            {assignedHouse}
          </h1>
          <p style={styles.welcomeMessage}>
            Welcome to {assignedHouse}! You have been sorted into one of Hogwarts' most distinguished houses.
          </p>
        </div>
        <button onClick={() => navigate('/home')} style={styles.primaryButton}>
          Continue to Home
        </button>
      </div>
    );
  }

  if (isPerformingCeremony) {
    return (
      <div style={styles.ceremonyContainer}>
        <h2 style={styles.ceremonyTitle}>The Sorting Hat is deciding...</h2>
        <img src="/images/sorting_hat.png" alt="Sorting Hat" style={styles.ceremonySortingHatImage} />
        <p style={styles.ceremonyMessage}>Hmm... let me see...</p>
      </div>
    );
  }

  return (
    <div style={styles.initialContainer}>
      <h2 style={styles.initialTitle}>The Sorting Hat</h2>
      <img
        src="/images/sorting_hat.png"
        alt="The Sorting Hat - Place upon your head to be sorted"
        style={styles.sortingHatImage}
      />
      <p style={styles.initialInstructions}>Place the hat upon your head and discover your house.</p>
      <div style={styles.buttonRow}>
        <button type="button" onClick={() => setShowHelp(true)} style={styles.helpButton}>
          Help
        </button>
        <button type="button" onClick={() => performHouseAssignment()} style={styles.primaryButton}>
          Put on the hat
        </button>
      </div>

      {showHelp && (
        <div style={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="sorting-help-title">
          <div style={styles.modal}>
            <h3 id="sorting-help-title" style={styles.modalTitle}>Help</h3>
            <p style={styles.modalText}>
              Most people should be sorted by the hat. If you need to manually join a house, press &quot;Manually Join&quot;.
            </p>
            <div style={styles.modalActions}>
              <button type="button" onClick={() => setShowHelp(false)} style={styles.dimButton}>
                Cancel
              </button>
              <button type="button" onClick={openManualJoin} style={styles.primaryButton}>
                Manually Join
              </button>
            </div>
          </div>
        </div>
      )}

      {showManualJoin && (
        <div style={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="manual-join-title">
          <div style={styles.wideModal}>
            <h3 id="manual-join-title" style={styles.modalTitle}>Choose your house</h3>
            <p style={styles.modalText}>Click the house you belong to.</p>
            {rosterError ? (
              <p style={styles.errorText}>{rosterError}</p>
            ) : (
              <div style={styles.houseGrid}>
                {HOGWARTS_HOUSES.map(houseName => {
                  const members = membersOf(houseName);
                  return (
                    <button
                      key={houseName}
                      type="button"
                      onClick={() => setConfirmHouse(houseName)}
                      style={{
                        ...styles.houseCard,
                        background: HOUSE_COLORS[houseName],
                        color: HOUSE_TEXT[houseName],
                      }}
                    >
                      <img
                        src={`/images/houses/${houseName.toLowerCase()}.png`}
                        alt=""
                        style={styles.houseCardCrest}
                      />
                      <span style={styles.houseCardName}>{houseName}</span>
                      <ul style={styles.memberList}>
                        {members.length === 0 ? (
                          <li style={styles.emptyMembers}>No one yet</li>
                        ) : (
                          members.map(member => <li key={member.id}>{member.name}</li>)
                        )}
                      </ul>
                    </button>
                  );
                })}
              </div>
            )}
            <div style={styles.modalActions}>
              <button
                type="button"
                onClick={() => {
                  setShowManualJoin(false);
                  setConfirmHouse(null);
                }}
                style={styles.dimButton}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmHouse && (
        <div style={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="confirm-house-title">
          <div style={styles.modal}>
            <h3 id="confirm-house-title" style={styles.modalTitle}>Join {confirmHouse}?</h3>
            <p style={styles.modalText}>
              Are you sure {confirmHouse} is the correct house?
            </p>
            <div style={styles.modalActions}>
              <button type="button" onClick={() => setConfirmHouse(null)} style={styles.dimButton}>
                Cancel
              </button>
              <button
                type="button"
                onClick={() => performHouseAssignment(confirmHouse)}
                style={styles.primaryButton}
              >
                Yes, join {confirmHouse}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  resultsContainer: {
    display: 'grid',
    gap: 24,
    justifyItems: 'center',
    textAlign: 'center',
  },
  announcementTitle: {
    color: '#eab308',
    fontSize: '2rem',
    margin: 0,
  },
  houseRevealSection: {
    display: 'grid',
    gap: 16,
    justifyItems: 'center',
  },
  houseCrestImage: {
    width: 120,
    height: 120,
    objectFit: 'contain',
  },
  houseNameTitle: {
    fontSize: '3rem',
    margin: 0,
    fontFamily: "'Cinzel Decorative', serif",
    color: '#e2e8f0',
  },
  welcomeMessage: {
    fontSize: '1.2rem',
    opacity: 0.8,
    maxWidth: 400,
  },
  ceremonyContainer: {
    display: 'grid',
    gap: 16,
    justifyItems: 'center',
    textAlign: 'center',
  },
  ceremonyTitle: {
    color: '#eab308',
  },
  ceremonySortingHatImage: {
    width: 160,
    height: 'auto',
    objectFit: 'contain',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  ceremonyMessage: {
    opacity: 0.7,
  },
  initialContainer: {
    display: 'grid',
    gap: 16,
    justifyItems: 'center',
  },
  initialTitle: {
    color: '#e2e8f0',
  },
  sortingHatImage: {
    width: 160,
    height: 'auto',
    objectFit: 'contain',
  },
  initialInstructions: {
    color: '#e2e8f0',
    opacity: 0.8,
  },
  buttonRow: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryButton: {
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid transparent',
    background: '#eab308',
    color: '#0b0f1a',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: '1rem',
  },
  helpButton: {
    padding: '10px 16px',
    borderRadius: 8,
    border: '1px solid #475569',
    background: 'transparent',
    color: '#94a3b8',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '1rem',
  },
  dimButton: {
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #475569',
    background: 'transparent',
    color: '#94a3b8',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '1rem',
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(2, 6, 23, 0.78)',
    display: 'grid',
    placeItems: 'center',
    padding: 16,
    zIndex: 40,
  },
  modal: {
    width: 'min(480px, 100%)',
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: 12,
    padding: 24,
    display: 'grid',
    gap: 16,
    textAlign: 'center',
  },
  wideModal: {
    width: 'min(960px, 100%)',
    maxHeight: '85vh',
    overflow: 'auto',
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: 12,
    padding: 24,
    display: 'grid',
    gap: 16,
    textAlign: 'center',
  },
  modalTitle: {
    margin: 0,
    color: '#e2e8f0',
  },
  modalText: {
    margin: 0,
    color: '#e2e8f0',
    opacity: 0.9,
    lineHeight: 1.5,
  },
  modalActions: {
    display: 'flex',
    gap: 12,
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  errorText: {
    margin: 0,
    color: '#fca5a5',
  },
  houseGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 12,
    textAlign: 'left',
  },
  houseCard: {
    border: 'none',
    borderRadius: 12,
    padding: 12,
    cursor: 'pointer',
    display: 'grid',
    gap: 8,
    justifyItems: 'center',
    minHeight: 180,
  },
  houseCardCrest: {
    width: 56,
    height: 56,
    objectFit: 'contain',
  },
  houseCardName: {
    fontWeight: 700,
    fontSize: '1.05rem',
  },
  memberList: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
    width: '100%',
    display: 'grid',
    gap: 4,
    fontSize: '0.92rem',
    textAlign: 'center',
  },
  emptyMembers: {
    opacity: 0.75,
  },
};
